import { Injectable } from '@angular/core';
import * as THREE from '@phdsoft/three';
import { SubtitleViewmodeService, LegendItem } from 'src/app/subtitle-viewmode/subtitle-viewmode.service';
import { Region } from 'src/app/platform/busines-data/Framework/Region';
import { SurfaceElement } from 'src/app/platform/busines-data/SectionElements/StructureElements/SurfaceElement';
import { LinearElement } from 'src/app/platform/busines-data/SectionElements/StructureElements/LinearElement';
import { RiskLevelItem } from 'src/app/admin/pages/risk-setup/types/risk-config.model';
import { RegionRisk } from 'src/app/template/critical-area-window/critical-area.service';

@Injectable({ providedIn: 'root' })
export class GraphicsMaterialsLegends {

  constructor(private subtitleService: SubtitleViewmodeService) {}


  public generateRegionsLegendLightModel(regions: Array<Region>, regionsRenderMaterial: Record<string, THREE.Material>): void {
    const regionColorMap = new Map<string, {
      regionName: string,
      hexColor: string,
      materialName: string,
      elements: string[]
    }>();

    for (const region of regions) {
      for (const obj of region.surfaceElements) {
        if (obj instanceof SurfaceElement) {
          const material = regionsRenderMaterial[obj.regionId];

          if (material && obj.regionId) {
            const regionId = obj.regionId;
            
            if (!regionColorMap.has(regionId)) {
              let colorHex = '#000000';
              
              if ((material as any).color) {
                colorHex = `#${(material as any).color.getHexString()}`;
              }

              regionColorMap.set(regionId, {
                regionName: region.Name || 'Unknown Region',
                hexColor: colorHex,
                materialName: material.name || 'Unnamed Material',
                elements: []
              });
            }

            const regionInfo = regionColorMap.get(regionId);
            regionInfo.elements.push(obj.Guid || 'Unknown Element');
          }
        }
      }
    }

    if (regionColorMap.size > 0) {
      const legendItems: LegendItem[] = Array.from(regionColorMap.entries()).map(([regionId, info]) => ({
        color: info.hexColor,
        hexColor: info.hexColor,
        value: regionId,
        description: info.regionName,
        label: info.regionName
      }));

      this.subtitleService.updateRegionsLegend(legendItems);
    } else {
      this.subtitleService.clearLegendData();
    }
  }

  public generateRegionsLegend(regions: Array<Region>, regionsRenderMaterial: Record<string, THREE.Material>): void {
    const regionColorMap = new Map<string, {
      regionName: string,
      hexColor: string,
      materialName: string,
      elements: string[]
    }>();

    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            if (!obj.realVisibility) {
              continue;
            }
            const material = regionsRenderMaterial[obj.regionId];

            if (material && obj.regionId) {
              const regionId = obj.regionId;
              
              if (!regionColorMap.has(regionId)) {
                let colorHex = '#000000';
                
                if ((material as any).color) {
                  colorHex = `#${(material as any).color.getHexString()}`;
                }

                regionColorMap.set(regionId, {
                  regionName: region.Name || 'Unknown Region',
                  hexColor: colorHex,
                  materialName: material.name || 'Unnamed Material',
                  elements: []
                });
              }

              const regionInfo = regionColorMap.get(regionId);
              regionInfo.elements.push(obj.Guid || 'Unknown Element');
            }
          }
        }
      }
    }

    if (regionColorMap.size > 0) {
      const legendItems: LegendItem[] = Array.from(regionColorMap.entries()).map(([regionId, info]) => ({
        color: info.hexColor,
        hexColor: info.hexColor,
        value: regionId,
        description: info.regionName,
        label: info.regionName
      }));

      this.subtitleService.updateRegionsLegend(legendItems);
    } else {
      this.subtitleService.clearLegendData();
    }
  }

  public generateMaterialsLegend(regions: Array<Region>, materialsRenderMaterial: Record<string, THREE.Material>): void {
    const materialColorMap = new Map<string, {
      materialTypeName: string,
      hexColor: string,
      materialName: string,
      elements: string[]
    }>();

    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement && obj.MaterialType) {
            if (!obj.realVisibility) {
              continue;
            }
            const material = materialsRenderMaterial[obj.MaterialType.Guid];

            if (material && obj.MaterialType) {
              const materialTypeGuid = obj.MaterialType.Guid;
              
              if (!materialColorMap.has(materialTypeGuid)) {
                let colorHex = '#000000';
                
                if ((material as any).color) {
                  colorHex = `#${(material as any).color.getHexString()}`;
                }

                const materialTypeName = obj.MaterialType.Name + ' ' + obj.MaterialType.materialSubType?.grade;

                materialColorMap.set(materialTypeGuid, {
                  materialTypeName: materialTypeName,
                  hexColor: colorHex,
                  materialName: material.name || 'Unnamed Material',
                  elements: []
                });
              }

              const materialInfo = materialColorMap.get(materialTypeGuid);
              materialInfo.elements.push(obj.Guid || 'Unknown Element');
            }
          }
        }
      }
    }

    if (materialColorMap.size > 0) {
      const legendItems: LegendItem[] = Array.from(materialColorMap.entries()).map(([guid, info]) => ({
        color: info.hexColor,
        hexColor: info.hexColor,
        value: guid,
        description: info.materialTypeName,
        label: info.materialTypeName
      }));

      this.subtitleService.updateMaterialsLegend(legendItems);
    } else {
      this.subtitleService.clearLegendData();
    }
  }

  public generateThicknessLegend(regions: Array<Region>, thicknessRenderMaterial: Record<string, THREE.Material>): void {
    const thicknessColorMap = new Map<number, {
      color: string,
      hexColor: string,
      materialName: string,
      elements: string[]
    }>();

    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            if (!obj.realVisibility) {
              continue;
            }
            const material = thicknessRenderMaterial[obj.thickness] ?? obj.defaultMaterial;

            if (material && obj.thickness !== undefined) {
              const thickness = obj.thickness;
              
              if (!thicknessColorMap.has(thickness)) {
                let colorHex = '#000000';
                let colorName = 'Unknown';
                
                if ((material as any).color) {
                  colorHex = `#${(material as any).color.getHexString()}`;
                }
                
                if (material.name) {
                  colorName = material.name;
                }

                thicknessColorMap.set(thickness, {
                  color: colorName,
                  hexColor: colorHex,
                  materialName: material.name || 'Unnamed Material',
                  elements: []
                });
              }

              const thicknessInfo = thicknessColorMap.get(thickness);
              thicknessInfo.elements.push(obj.Guid || 'Unknown Element');
            }
          }
        }
      }
    }

    if (thicknessColorMap.size > 0) {
      const legendItems: LegendItem[] = Array.from(thicknessColorMap.entries()).map(([thickness, info]) => ({
        color: info.hexColor,
        hexColor: info.hexColor,
        value: thickness,
        description: `${thickness}mm`,
        label: `${thickness}mm`
      }));

      this.subtitleService.updateThicknessLegend(legendItems);
    } else {
      this.subtitleService.clearLegendData();
    }
  }

  public generateRiskLegend(regionsRiskLevels: RegionRisk[], riskLevels: RiskLevelItem[]): void {
    
    if (!riskLevels || riskLevels.length === 0) {
      this.subtitleService.clearLegendData();
      return;
    }

    const sortedRiskLevels = [...riskLevels].sort((a, b) => a.order - b.order);

    const legendItems: LegendItem[] = sortedRiskLevels.map(riskLevel => {
      const color = new THREE.Color(riskLevel.color);
      
      // HSL in sRGB, as three r117 did — must match GraphicsMaterials.adjustColorVividness
      const hsl = { h: 0, s: 0, l: 0 };
      // color.getHSL(hsl);
      color.getHSL(hsl, THREE.SRGBColorSpace);
      hsl.s = Math.min(hsl.s, 0.7);
      hsl.l = Math.max(Math.min(hsl.l, 0.7), 0.3);
      // const adjustedColor = new THREE.Color().setHSL(hsl.h, hsl.s, hsl.l);
      const adjustedColor = new THREE.Color().setHSL(hsl.h, hsl.s, hsl.l, THREE.SRGBColorSpace);

      return {
        color: `#${adjustedColor.getHexString()}`,
        hexColor: `#${adjustedColor.getHexString()}`,
        value: riskLevel.order,
        description: riskLevel.name,
        label: riskLevel.name
      };
    });
    this.subtitleService.updateRiskLegend(legendItems);
  }

  public generateDegradationLegend(regions: Array<Region>): void {
    const degradationColorMap = new Map<string, {
      levelName: string,
      hexColor: string,
      materialName: string,
      order: number,
      elements: string[]
    }>();

    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            if (!obj.realVisibility) {
              continue;
            }
            
            const material = obj.currentMaterial || obj.SurfaceDesign.material;
            if (!material || Array.isArray(material)) continue;

            const threeMaterial = material as THREE.Material;
            const order = this.getCorrosionViewModeOrder(threeMaterial.name);
            if (!order) {
              continue;
            }

            let levelName = 'Unknown';
            if (order === 1) {
              levelName = 'Acceptable Corrosion';
            } else if (order === 2) {
              levelName = 'Substantial Corrosion';
            } else if (order === 3) {
              levelName = 'Excessive Corrosion';
            }

            if (!degradationColorMap.has(levelName)) {
              let colorHex = '#000000';
              
              if ((threeMaterial as any).color) {
                colorHex = `#${(threeMaterial as any).color.getHexString()}`;
              }

              degradationColorMap.set(levelName, {
                levelName: levelName,
                hexColor: colorHex,
                materialName: threeMaterial.name || 'Unnamed Material',
                order: order,
                elements: []
              });
            }

            const degradationInfo = degradationColorMap.get(levelName);
            degradationInfo.elements.push(obj.Guid || 'Unknown Element');
          }
        }
      }
    }

    if (degradationColorMap.size > 0) {
      const legendItems: LegendItem[] = Array.from(degradationColorMap.entries())
        .map(([levelName, info]) => ({
          color: info.hexColor,
          hexColor: info.hexColor,
          value: info.order,
          description: info.levelName,
          label: info.levelName
        }))
        .sort((a, b) => (a.value as number) - (b.value as number));

      this.subtitleService.updateDegradationLegend(legendItems);
    } else {
      this.subtitleService.clearLegendData();
    }
  }

  public generateRemainingLifeLegend(regions: Array<Region>): void {
    const remainingLifeColorMap = new Map<string, {
      levelName: string,
      hexColor: string,
      materialName: string,
      order: number,
      elements: string[]
    }>();

    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            if (!obj.realVisibility) {
              continue;
            }
            
            const material = obj.currentMaterial || obj.SurfaceDesign.material;
            if (!material || Array.isArray(material)) continue;

            const threeMaterial = material as THREE.Material;
            const order = this.getCorrosionViewModeOrder(threeMaterial.name);
            if (!order) {
              continue;
            }

            let levelName = 'Unknown';
            if (order === 1) {
              levelName = 'Remaining Life\n(≥25 years)';
            } else if (order === 2) {
              levelName = 'Remaining Life\n(5-25 years)';
            } else if (order === 3) {
              levelName = 'Remaining Life\n(<5 years)';
            }

            if (!remainingLifeColorMap.has(levelName)) {
              let colorHex = '#000000';
              
              if ((threeMaterial as any).color) {
                colorHex = `#${(threeMaterial as any).color.getHexString()}`;
              }

              remainingLifeColorMap.set(levelName, {
                levelName: levelName,
                hexColor: colorHex,
                materialName: threeMaterial.name || 'Unnamed Material',
                order: order,
                elements: []
              });
            }

            const remainingLifeInfo = remainingLifeColorMap.get(levelName);
            remainingLifeInfo.elements.push(obj.Guid || 'Unknown Element');
          }
        }
      }
    }

    if (remainingLifeColorMap.size > 0) {
      const legendItems: LegendItem[] = Array.from(remainingLifeColorMap.entries())
        .map(([levelName, info]) => ({
          color: info.hexColor,
          hexColor: info.hexColor,
          value: info.order,
          description: info.levelName,
          label: info.levelName
        }))
        .sort((a, b) => (a.value as number) - (b.value as number));

      this.subtitleService.updateRemainingLifeLegend(legendItems);
    } else {
      this.subtitleService.clearLegendData();
    }
  }

  public generateCorrosionRateLegend(regions: Array<Region>): void {
    const corrosionRateColorMap = new Map<string, {
      levelName: string,
      hexColor: string,
      materialName: string,
      order: number,
      elements: string[]
    }>();

    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            if (!obj.realVisibility) {
              continue;
            }
            
            const material = obj.currentMaterial || obj.SurfaceDesign.material;
            if (!material || Array.isArray(material)) continue;

            const threeMaterial = material as THREE.Material;
            const order = this.getCorrosionViewModeOrder(threeMaterial.name);
            if (!order) {
              continue;
            }

            let levelName = 'Unknown';
            if (order === 1) {
              levelName = 'Low Corrosion Rate\n(≤0.2 mm/y)';
            } else if (order === 2) {
              levelName = 'Medium Corrosion Rate\n(0.2-0.6 mm/y)';
            } else if (order === 3) {
              levelName = 'High Corrosion Rate\n(>0.6 mm/y)';
            }

            if (!corrosionRateColorMap.has(levelName)) {
              let colorHex = '#000000';
              
              if ((threeMaterial as any).color) {
                colorHex = `#${(threeMaterial as any).color.getHexString()}`;
              }

              corrosionRateColorMap.set(levelName, {
                levelName: levelName,
                hexColor: colorHex,
                materialName: threeMaterial.name || 'Unnamed Material',
                order: order,
                elements: []
              });
            }

            const corrosionRateInfo = corrosionRateColorMap.get(levelName);
            corrosionRateInfo.elements.push(obj.Guid || 'Unknown Element');
          }
        }
      }
    }

    if (corrosionRateColorMap.size > 0) {
      const legendItems: LegendItem[] = Array.from(corrosionRateColorMap.entries())
        .map(([levelName, info]) => ({
          color: info.hexColor,
          hexColor: info.hexColor,
          value: info.order,
          description: info.levelName,
          label: info.levelName
        }))
        .sort((a, b) => (a.value as number) - (b.value as number));

      this.subtitleService.updateCorrosionRateLegend(legendItems);
    } else {
      this.subtitleService.clearLegendData();
    }
  }

  public generateCorrosionRiskIndexLegend(regions: Array<Region>): void {
    const corrosionRiskIndexColorMap = new Map<string, {
      levelName: string,
      hexColor: string,
      materialName: string,
      order: number,
      elements: string[]
    }>();

    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            if (!obj.realVisibility) {
              continue;
            }
            
            const material = obj.currentMaterial || obj.SurfaceDesign.material;
            if (!material || Array.isArray(material)) continue;

            const threeMaterial = material as THREE.Material;
            const order = this.getCorrosionViewModeOrder(threeMaterial.name);
            if (!order) {
              continue;
            }

            let levelName = 'Unknown';
            if (order === 1) {
              levelName = 'Low Risk Index\n(≤15)';
            } else if (order === 2) {
              levelName = 'Medium Risk Index\n(15-20)';
            } else if (order === 3) {
              levelName = 'High Risk Index\n(>20)';
            }

            if (!corrosionRiskIndexColorMap.has(levelName)) {
              let colorHex = '#000000';
              
              if ((threeMaterial as any).color) {
                colorHex = `#${(threeMaterial as any).color.getHexString()}`;
              }

              corrosionRiskIndexColorMap.set(levelName, {
                levelName: levelName,
                hexColor: colorHex,
                materialName: threeMaterial.name || 'Unnamed Material',
                order: order,
                elements: []
              });
            }

            const corrosionRiskIndexInfo = corrosionRiskIndexColorMap.get(levelName);
            corrosionRiskIndexInfo.elements.push(obj.Guid || 'Unknown Element');
          }
        }
      }
    }

    if (corrosionRiskIndexColorMap.size > 0) {
      const legendItems: LegendItem[] = Array.from(corrosionRiskIndexColorMap.entries())
        .map(([levelName, info]) => ({
          color: info.hexColor,
          hexColor: info.hexColor,
          value: info.order,
          description: info.levelName,
          label: info.levelName
        }))
        .sort((a, b) => (a.value as number) - (b.value as number));

      this.subtitleService.updateCorrosionRiskIndexLegend(legendItems);
    } else {
      this.subtitleService.clearLegendData();
    }
  }

  public generateStructure4DLegend(regions: Array<Region>, structure4DMaterials: Record<string, THREE.Material>): void {
    const dateColorMap = new Map<string, {
      dateLabel: string,
      hexColor: string,
      elements: string[]
    }>();

    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.surfaceElements) {
          if (obj instanceof SurfaceElement) {
            if (!obj.realVisibility) {
              continue;
            }
            const dateKey = this.normalizeStructure4DDate(obj.properties?.General?.Collection?.Date);
            if (!dateKey) continue;

            const material = structure4DMaterials[dateKey];
            if (!material) continue;

            if (!dateColorMap.has(dateKey)) {
              let colorHex = '#000000';
              if ((material as any).color) {
                colorHex = `#${(material as any).color.getHexString()}`;
              }

              dateColorMap.set(dateKey, {
                dateLabel: this.formatStructure4DDateLabel(dateKey),
                hexColor: colorHex,
                elements: []
              });
            }

            const dateInfo = dateColorMap.get(dateKey);
            dateInfo.elements.push(obj.Guid || 'Unknown Element');
          }
        }
      }
    }

    if (dateColorMap.size > 0) {
      const legendItems: LegendItem[] = Array.from(dateColorMap.entries())
        .map(([dateKey, info]) => ({
          color: info.hexColor,
          hexColor: info.hexColor,
          value: dateKey,
          description: info.dateLabel,
          label: info.dateLabel
        }))
        .sort((a, b) => String(a.value).localeCompare(String(b.value)));

      this.subtitleService.updateStructure4DLegend(legendItems);
    } else {
      this.subtitleService.clearLegendData();
    }
  }

  public generateTypicalDetailsFamilyLegend(regions: Array<Region>, familyRenderMaterial: Record<string, THREE.Material>): void {
    const familyColorMap = new Map<string, {
      familyId: string,
      hexColor: string,
      elements: string[]
    }>();

    for (const region of regions) {
      for (const section of region.sections) {
        for (const obj of section.linearElements) {
          if (obj instanceof LinearElement) {
            if (!obj.realVisibility) {
              continue;
            }

            const typicalDetail = obj.properties?.TypicalDetail;
            if (!typicalDetail?.FamilyGuid) {
              continue;
            }

            const material = familyRenderMaterial[typicalDetail.FamilyGuid];
            if (!material) {
              continue;
            }

            if (!familyColorMap.has(typicalDetail.FamilyGuid)) {
              let colorHex = '#000000';

              if ((material as any).color) {
                colorHex = `#${(material as any).color.getHexString()}`;
              }

              familyColorMap.set(typicalDetail.FamilyGuid, {
                familyId: typicalDetail.FamilyId || typicalDetail.FamilyGuid,
                hexColor: colorHex,
                elements: []
              });
            }

            const familyInfo = familyColorMap.get(typicalDetail.FamilyGuid);
            familyInfo.elements.push(obj.Guid || 'Unknown Element');
          }
        }
      }
    }

    if (familyColorMap.size > 0) {
      const legendItems: LegendItem[] = Array.from(familyColorMap.entries()).map(([familyGuid, info]) => ({
        color: info.hexColor,
        hexColor: info.hexColor,
        value: familyGuid,
        description: info.familyId,
        label: info.familyId
      }));

      this.subtitleService.updateTypicalDetailsFamilyLegend(legendItems);
    } else {
      this.subtitleService.clearLegendData();
    }
  }

  public clearLegend(): void {
    this.subtitleService.clearLegendData();
  }

  private normalizeStructure4DDate(value: unknown): string | undefined {
    if (!value) {
      return undefined;
    }

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return undefined;
      }
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    const text = String(value).trim();
    const isoPrefixMatch = text.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoPrefixMatch) {
      return isoPrefixMatch[1];
    }

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) {
      return undefined;
    }

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatStructure4DDateLabel(dateKey: string): string {
    const dateMatch = dateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!dateMatch) {
      return dateKey;
    }

    const [, year, month, day] = dateMatch;
    return `${day}/${month}/${year}`;
  }

  private getCorrosionViewModeOrder(materialName: string): number | null {
    switch (materialName) {
      case 'GreenMaterial':
      case 'AcceptableCorrosionViewModeMaterial':
        return 1;
      case 'YellowMaterial':
      case 'SubstantialCorrosionViewModeMaterial':
        return 2;
      case 'RedMaterial':
      case 'ExcessiveCorrosionViewModeMaterial':
        return 3;
      default:
        return null;
    }
  }
}
